'use client';
import { useUser, useUserService, useLoyaltyService, getMediaOrigin } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';

const PROFILE_BUTTON_TEST_ID = 'profile-button';

const PROFILE_IMAGE_PATHS = {
  USER_LOGO: '/profile-logo-images/user-logo-image.png',
  GUEST_LOGO: '/profile-logo-images/guest-logo.png',
} as const;

interface ProfileButtonProps {
  className?: string;
}

export const ProfileButton = ({ className, ...rest }: ProfileButtonProps) => {
  const { isAuthenticated, isLoading, user: currentUser } = useUser();
  const { useGetUserMe } = useUserService();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: user, isLoading: isLoadingUser } = useGetUserMe({ enabled: isAuthenticated });
  const { data: balanceData } = useGetCustomerBalance({
    customerId: String(currentUser?.userId ?? ''),
    enabled: isAuthenticated && Boolean(currentUser?.userId),
  });

  const displayName = user?.displayName || 'Guest';
  const points = balanceData?.availablePoints ?? 0;

  if (isLoading || isLoadingUser) {
    return null;
  }

  return (
    <div
      className={`items-center cursor-pointer hidden tablet:flex gap-2 min-w-fit shrink-0 ${className || ''}`}
      data-testid={PROFILE_BUTTON_TEST_ID}
      {...rest}
    >
      {/* Profile Picture */}
      <div className="w-[26px] h-[26px] rounded-full overflow-hidden shrink-0 relative">
        <Condition
          expression={isAuthenticated}
          then={
            <img
              src={`${getMediaOrigin()}${PROFILE_IMAGE_PATHS.USER_LOGO}`}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          }
          else={
            <img
              src={`${getMediaOrigin()}${PROFILE_IMAGE_PATHS.GUEST_LOGO}`}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          }
        />
      </div>

      {/* Text Content */}
      <div className="flex flex-col gap-[2px] items-start">
        <Condition
          expression={isAuthenticated}
          then={
            <div className="flex flex-col gap-[2px] items-start max-w-[100px] w-full">
              <p className="text-label-small font-semibold text-generic-0 truncate w-full">Hi, {displayName}</p>
              <p className="text-label-xsmall font-normal text-primary-200 truncate w-full">{points} pts</p>
            </div>
          }
          else={
            <>
              <p className="text-label-small font-semibold text-generic-0 whitespace-nowrap">Sign In</p>
              <p className="text-label-xsmall font-normal text-neutral-400 whitespace-nowrap">For points!</p>
            </>
          }
        />
      </div>
    </div>
  );
};

export default ProfileButton;
